import {
  Component,
  HostListener,
  OnInit,
  computed,
  inject,
  signal,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Observable, finalize, forkJoin, of, switchMap, take } from 'rxjs';
import {
  AtualizarPerfilRequest,
  CriarPerfilRequest,
  PerfilStatus,
} from '../../core/models/perfil.model';
import { mapStatusToApi } from '../../core/mappers/perfil.mapper';
import { PerfilService } from '../../core/services/perfil.service';
import { ApiHttpError } from '../../shared/services/http-client/api-http-error.model';
import { AlertDialogComponent } from '../../shared/components/alert-dialog/alert-dialog.component';
import { BackButtonComponent } from '../../shared/components/back-button/back-button.component';
import { ToastService } from '../../core/services/toast.service';
import { LoaderService } from '../../shared/services/loader/loader.service';

interface CorOption {
  readonly label: string;
  readonly valor: string;
}

interface PieSlice {
  readonly cor: string;
  readonly dashArray: string;
  readonly dashOffset: number;
}

/** Item de categoria manipulado no formulário (alinhado ao modelo de domínio). */
interface CategoriaForm {
  /**
   * Identidade da categoria na API, usada como chave do PUT/DELETE. Permanece
   * fixa mesmo que o `nomeCategoria` seja renomeado. `undefined` indica
   * categoria nova (ainda não persistida).
   */
  idCategoria?: number;
  nomeCategoria: string;
  cor: string;
  valorPercentual: number;
}

/**
 * O gráfico de pizza é desenhado com um único <circle> por categoria usando a
 * técnica de stroke-dasharray. Com `pathLength=100` a circunferência é
 * normalizada para 100, então é possível trabalhar diretamente em porcentagens.
 */
const PIE_PATH_LENGTH = 100;
const PIE_START_OFFSET = 25; // inicia a primeira fatia no topo (12h)

const MOTIVO_BLOQUEIO_EDICAO_PADRAO = 'Este perfil não pode ser editado.';

@Component({
  selector: 'app-perfil-cadastro',
  standalone: true,
  imports: [ReactiveFormsModule, BackButtonComponent, AlertDialogComponent],
  templateUrl: './perfil-cadastro.component.html',
  styleUrl: './perfil-cadastro.component.scss',
})
export class PerfilCadastroComponent implements OnInit {
  private readonly fb = inject(FormBuilder);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly perfilService = inject(PerfilService);
  private readonly toastService = inject(ToastService);
  private readonly loaderService = inject(LoaderService);

  /** Modo de edição (true) ou cadastro (false). */
  isEditMode = false;
  /** ID do perfil em edição. */
  perfilId?: number;

  /** Snapshot das categorias carregadas (base para o diff de sincronização). */
  private categoriasOriginais: CategoriaForm[] = [];

  /** Paleta baseada nos tokens de cor do Design System. */
  readonly coresDisponiveis: readonly CorOption[] = [
    { label: 'Roxo', valor: '#3c2e88' },
    { label: 'Vermelho', valor: '#e33e5a' },
    { label: 'Verde', valor: '#00d173' },
    { label: 'Amarelo', valor: '#f0c93a' },
    { label: 'Cinza', valor: '#7d7d7d' },
    { label: 'Roxo claro', valor: '#a59fc8' },
    { label: 'Azul', valor: '#2563eb' },
    { label: 'Azul claro', valor: '#60a5fa' },
    { label: 'Laranja', valor: '#f97316' },
    { label: 'Rosa', valor: '#ec4899' },
    { label: 'Marrom', valor: '#8b5e3c' },
    { label: 'Preto', valor: '#000000' },
  ];

  readonly pathLength = PIE_PATH_LENGTH;

  /** Limite máximo de caracteres da descrição do perfil. */
  readonly descricaoMaxLength = 1000;

  /** Dados gerais do perfil. */
  readonly form: FormGroup = this.fb.group({
    nome: ['', Validators.required],
    nomeExibicao: [''],
    descricao: ['', [Validators.required, Validators.maxLength(this.descricaoMaxLength)]],
    status: ['Ativo' as PerfilStatus],
  });

  /** Sub-formulário da linha "Adicione categorias para o perfil". */
  readonly novaCategoria: FormGroup = this.fb.group({
    cor: [this.coresDisponiveis[0].valor],
    nome: ['', Validators.required],
    valor: [
      null as number | null,
      [Validators.required, Validators.min(0), Validators.max(100)],
    ],
  });

  // Inicia vazio no modo cadastro; preenchido em carregarPerfil() na edição.
  private readonly categoriasSignal = signal<CategoriaForm[]>([]);

  readonly categorias = this.categoriasSignal.asReadonly();
  readonly submitAttempted = signal(false);
  readonly addAttempted = signal(false);

  /** Controle do dropdown customizado de cores. */
  readonly corDropdownAberto = signal(false);

  /** Cor atualmente selecionada (acompanha o FormControl, inclusive em reset). */
  readonly corAtual = toSignal(this.novaCategoria.controls['cor'].valueChanges, {
    initialValue: this.novaCategoria.controls['cor'].value as string,
  });

  readonly corAtualLabel = computed(() => this.corLabel(this.corAtual()));

  /** Status atual selecionado no toggle Ativar/Desativar. */
  readonly statusAtual = toSignal(this.form.controls['status'].valueChanges, {
    initialValue: this.form.controls['status'].value as PerfilStatus,
  });

  /** Validade reativa do formulário (zoneless-safe). */
  private readonly formStatus = toSignal(this.form.statusChanges, {
    initialValue: this.form.status,
  });

  /** Comprimento atual da descrição (para o contador de caracteres). */
  private readonly descricaoValue = toSignal(
    this.form.controls['descricao'].valueChanges,
    { initialValue: this.form.controls['descricao'].value as string },
  );

  readonly descricaoLength = computed(() => (this.descricaoValue() ?? '').length);

  /** Um perfil sem categoria não distribui investimento nenhum: ao menos uma é obrigatória. */
  readonly semCategorias = computed(() => this.categorias().length === 0);

  /** Índice da linha aguardando confirmação de exclusão; `null` fecha o diálogo. */
  private readonly indiceParaExcluir = signal<number | null>(null);

  readonly categoriaParaExcluir = computed(() => {
    const index = this.indiceParaExcluir();
    return index == null ? undefined : this.categorias()[index];
  });

  /** Preenchidos a partir de `permiteEdicao`/`motivoBloqueioEdicao` da consulta do perfil. */
  readonly edicaoBloqueada = signal(false);
  readonly motivoBloqueioEdicao = signal('');

  readonly podeSalvar = computed(
    () =>
      !this.edicaoBloqueada() &&
      this.formStatus() === 'VALID' &&
      !this.semCategorias() &&
      !this.distribuicaoInvalida(),
  );

  get pageTitle(): string {
    return this.isEditMode ? 'Editar Tipo Perfil' : 'Criar Tipo Perfil';
  }

  get salvarLabel(): string {
    return this.isEditMode ? 'Salvar Alterações' : 'Salvar';
  }

  setStatus(status: PerfilStatus): void {
    this.form.controls['status'].setValue(status);
  }

  /** Alterna entre Ativo e Inativo. */
  toggleDesativar(): void {
    this.setStatus(this.statusAtual() === 'Inativo' ? 'Ativo' : 'Inativo');
  }

  onExcluir(): void {
    if (!this.perfilId) {
      void this.router.navigate(['/perfis']);
      return;
    }

    this.loaderService.showLoader();
    this.perfilService
      .deletePerfil(this.perfilId)
      .pipe(take(1), finalize(() => this.loaderService.hideLoader()))
      .subscribe({
        next: () => {
          this.toastService.show('Perfil excluído com sucesso.', 'success');
          void this.router.navigate(['/perfis']);
        },
        error: (error: unknown) => {
          const message =
            error instanceof ApiHttpError ? error.message : 'Não foi possível excluir o perfil.';
          this.toastService.show(message, 'error');
        },
      });
  }

  ngOnInit(): void {
    const idParam = this.route.snapshot.paramMap.get('id');
    if (idParam) {
      this.isEditMode = true;
      this.perfilId = Number(idParam);
      this.carregarPerfil(this.perfilId);
    }
  }

  carregarPerfil(id: number): void {
    this.loaderService.showLoader();
    forkJoin({
      perfil: this.perfilService.getPerfilById(id),
      categorias: this.perfilService.getCategoriasByPerfil(id),
    })
      .pipe(
        take(1),
        finalize(() => this.loaderService.hideLoader()),
      )
      .subscribe({
        next: ({ perfil, categorias: categoriasPerfil }) => {
          if (!perfil) {
            // Perfil inexistente: volta para a listagem.
            void this.router.navigate(['/perfis']);
            return;
          }

          this.form.patchValue({
            nome: perfil.nome,
            nomeExibicao: perfil.nomeExibicao,
            descricao: perfil.descricao,
            status: perfil.status,
          });

          const categorias: CategoriaForm[] = categoriasPerfil.map((categoria) => ({
            idCategoria: categoria.idCategoria,
            nomeCategoria: categoria.nomeCategoria,
            cor: categoria.cor,
            valorPercentual: categoria.valorPercentual,
          }));
          this.categoriasSignal.set(categorias);
          this.categoriasOriginais = categorias.map((categoria) => ({ ...categoria }));

          if (perfil.permiteEdicao === false) {
            this.bloquearEdicao(perfil.motivoBloqueioEdicao ?? '');
          }
        },
        error: () => {
          this.toastService.show('Não foi possível carregar o perfil.', 'error');
          void this.router.navigate(['/perfis']);
        },
      });
  }

  /** Desabilita os formulários; os controles das categorias leem `edicaoBloqueada` no template. */
  private bloquearEdicao(motivo: string): void {
    this.edicaoBloqueada.set(true);
    this.motivoBloqueioEdicao.set(motivo || MOTIVO_BLOQUEIO_EDICAO_PADRAO);
    this.form.disable();
    this.novaCategoria.disable();
  }

  readonly totalValor = computed(() =>
    this.categorias().reduce(
      (soma, categoria) => soma + (categoria.valorPercentual || 0),
      0,
    ),
  );

  readonly distribuicaoInvalida = computed(
    () => this.categorias().length > 0 && this.totalValor() !== 100,
  );

  /** Fatias do gráfico de pizza, normalizadas pelo total das categorias. */
  readonly pieSlices = computed<PieSlice[]>(() => {
    const categorias = this.categorias();
    const total = this.totalValor();
    if (total <= 0) {
      return [];
    }

    let acumulado = 0;
    return categorias.map((categoria) => {
      const percentual = ((categoria.valorPercentual || 0) / total) * PIE_PATH_LENGTH;
      const slice: PieSlice = {
        cor: categoria.cor,
        dashArray: `${percentual} ${PIE_PATH_LENGTH - percentual}`,
        dashOffset: PIE_START_OFFSET - acumulado,
      };
      acumulado += percentual;
      return slice;
    });
  });

  corLabel(valor: string): string {
    return this.coresDisponiveis.find((cor) => cor.valor === valor)?.label ?? valor;
  }

  toggleCorDropdown(event: Event): void {
    event.stopPropagation();
    this.corDropdownAberto.update((aberto) => !aberto);
  }

  selecionarCor(valor: string): void {
    this.novaCategoria.controls['cor'].setValue(valor);
    this.corDropdownAberto.set(false);
  }

  @HostListener('document:click')
  fecharCorDropdown(): void {
    this.corDropdownAberto.set(false);
  }

  showControlError(controlName: string): boolean {
    const control = this.form.controls[controlName];
    return control.invalid && (this.submitAttempted() || control.touched);
  }

  showNovaCategoriaError(controlName: string): boolean {
    const control = this.novaCategoria.controls[controlName];
    return control.invalid && (this.addAttempted() || control.touched);
  }

  adicionarCategoria(): void {
    this.addAttempted.set(true);
    this.novaCategoria.markAllAsTouched();

    if (this.novaCategoria.invalid) {
      return;
    }

    const { cor, nome, valor } = this.novaCategoria.getRawValue() as {
      cor: string;
      nome: string;
      valor: number;
    };

    this.categoriasSignal.update((categorias) => [
      ...categorias,
      { cor, nomeCategoria: nome.trim(), valorPercentual: Number(valor) },
    ]);

    this.novaCategoria.reset({ cor: this.coresDisponiveis[0].valor, nome: '', valor: null });
    this.addAttempted.set(false);
  }

  /** O "-" só pede a confirmação; quem exclui de fato é `confirmarExclusaoCategoria`. */
  removerCategoria(index: number): void {
    this.indiceParaExcluir.set(index);
  }

  cancelarExclusaoCategoria(): void {
    this.indiceParaExcluir.set(null);
  }

  confirmarExclusaoCategoria(): void {
    const index = this.indiceParaExcluir();
    const categoria = this.categoriaParaExcluir();
    this.indiceParaExcluir.set(null);

    if (index == null || !categoria) {
      return;
    }

    // Categoria que ainda não foi gravada (ou perfil em criação): não há o que excluir na API.
    if (categoria.idCategoria == null || !this.perfilId) {
      this.tirarCategoriaDaLista(index);
      return;
    }

    this.excluirCategoriaNaApi(this.perfilId, categoria.idCategoria, index);
  }

  private excluirCategoriaNaApi(idPerfil: number, idCategoria: number, index: number): void {
    this.loaderService.showLoader();

    this.perfilService
      .deleteCategoria(idPerfil, idCategoria)
      .pipe(take(1), finalize(() => this.loaderService.hideLoader()))
      .subscribe({
        next: () => {
          this.tirarCategoriaDaLista(index);
          // Sai também do snapshot: senão o diff do salvar tentaria excluí-la de novo.
          this.categoriasOriginais = this.categoriasOriginais.filter(
            (original) => original.idCategoria !== idCategoria,
          );
          this.toastService.show('Categoria excluída com sucesso.', 'success');
        },
        error: (error: unknown) => {
          const message =
            error instanceof ApiHttpError
              ? error.message
              : 'Não foi possível excluir a categoria.';
          this.toastService.show(message, 'error');
        },
      });
  }

  private tirarCategoriaDaLista(index: number): void {
    this.categoriasSignal.update((categorias) =>
      categorias.filter((_, i) => i !== index),
    );
  }

  atualizarNomeCategoria(index: number, valor: string): void {
    this.categoriasSignal.update((categorias) =>
      categorias.map((categoria, i) =>
        i === index ? { ...categoria, nomeCategoria: valor } : categoria,
      ),
    );
  }

  atualizarCorCategoria(index: number, valor: string): void {
    this.categoriasSignal.update((categorias) =>
      categorias.map((categoria, i) =>
        i === index ? { ...categoria, cor: valor } : categoria,
      ),
    );
  }

  atualizarValorCategoria(index: number, valor: string): void {
    const numero = Number(valor);
    this.categoriasSignal.update((categorias) =>
      categorias.map((categoria, i) =>
        i === index
          ? { ...categoria, valorPercentual: Number.isFinite(numero) ? numero : 0 }
          : categoria,
      ),
    );
  }

  voltar(): void {
    void this.router.navigate(['/perfis']);
  }

  onCancelar(): void {
    void this.router.navigate(['/perfis']);
  }

  salvar(): void {
    this.submitAttempted.set(true);
    this.form.markAllAsTouched();

    if (!this.podeSalvar()) {
      return;
    }

    const { nome, nomeExibicao, descricao, status } = this.form.getRawValue() as {
      nome: string;
      nomeExibicao: string;
      descricao: string;
      status: PerfilStatus;
    };

    const sucessoMsg = this.isEditMode
      ? 'Perfil atualizado com sucesso.'
      : 'Perfil criado com sucesso.';

    this.loaderService.showLoader();

    const persistir$: Observable<unknown> =
      this.isEditMode && this.perfilId != null
        ? this.atualizarPerfil(this.perfilId, {
            idPerfilInvestimento: this.perfilId,
            nome,
            nomeExibicao,
            descricao,
            status: mapStatusToApi(status),
          })
        : this.criarPerfil({ nome, nomeExibicao, descricao });

    persistir$
      .pipe(
        take(1),
        finalize(() => this.loaderService.hideLoader()),
      )
      .subscribe({
        next: () => {
          this.toastService.show(sucessoMsg, 'success');
          void this.router.navigate(['/perfis']);
        },
        error: (error: unknown) => {
          const message =
            error instanceof ApiHttpError ? error.message : 'Não foi possível salvar o perfil.';
          this.toastService.show(message, 'error');
        },
      });
  }

  /** Cria o perfil e, em seguida, cada categoria do formulário. */
  private criarPerfil(payload: CriarPerfilRequest): Observable<unknown> {
    return this.perfilService
      .createPerfil(payload)
      .pipe(switchMap((perfil) => this.criarCategorias(perfil.id)));
  }

  /** Atualiza o perfil e sincroniza as categorias (create/update/delete). */
  private atualizarPerfil(
    idPerfil: number,
    payload: AtualizarPerfilRequest,
  ): Observable<unknown> {
    return this.perfilService
      .updatePerfil(idPerfil, payload)
      .pipe(switchMap(() => this.sincronizarCategorias(idPerfil)));
  }

  private criarCategorias(idPerfil: number): Observable<unknown> {
    const ops = this.categorias().map((categoria) =>
      this.perfilService.createCategoria(idPerfil, {
        idPerfilInvestimento: idPerfil,
        nome: categoria.nomeCategoria,
        cor: categoria.cor,
        valorPercentual: categoria.valorPercentual,
      }),
    );

    return ops.length > 0 ? forkJoin(ops) : of(undefined);
  }

  /**
   * Diff entre categorias atuais e originais (identidade = `idCategoria`):
   * nova (sem id) → POST; alterada (nome/cor/valor) → PUT no id; removida →
   * DELETE no id. A identidade sobrevive a renomeações.
   */
  private sincronizarCategorias(idPerfil: number): Observable<unknown> {
    const atuais = this.categorias();
    const originaisPorId = new Map(
      this.categoriasOriginais
        .filter((categoria) => categoria.idCategoria != null)
        .map((categoria) => [categoria.idCategoria as number, categoria]),
    );
    const idsPresentes = new Set<number>();

    const ops: Observable<unknown>[] = [];

    for (const categoria of atuais) {
      const body = {
        idPerfilInvestimento: idPerfil,
        idCategoria: categoria.idCategoria,
        nome: categoria.nomeCategoria,
        cor: categoria.cor,
        valorPercentual: categoria.valorPercentual,
      };

      const original =
        categoria.idCategoria != null
          ? originaisPorId.get(categoria.idCategoria)
          : undefined;

      if (!original || original.idCategoria == null) {
        // Categoria nova (sem id correspondente) → POST.
        ops.push(this.perfilService.createCategoria(idPerfil, body));
        continue;
      }

      idsPresentes.add(original.idCategoria);

      const alterada =
        original.nomeCategoria !== categoria.nomeCategoria ||
        original.cor !== categoria.cor ||
        original.valorPercentual !== categoria.valorPercentual;

      if (alterada) {
        ops.push(
          this.perfilService.updateCategoria(idPerfil, original.idCategoria, body),
        );
      }
    }

    for (const original of this.categoriasOriginais) {
      if (original.idCategoria != null && !idsPresentes.has(original.idCategoria)) {
        ops.push(this.perfilService.deleteCategoria(idPerfil, original.idCategoria));
      }
    }

    return ops.length > 0 ? forkJoin(ops) : of(undefined);
  }
}
